import React, { useMemo } from 'react'
import classNames from 'classnames'
import { RichTextDocument } from 'shared-types'
import { Image } from '~/components/Image'
import { getImageSrc } from '~/helpers'
import { Link } from '../Link'
import RichText from '../RichText/RichText'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './DisruptorPromoCard.module.scss'
import { DisruptorPromoCardProps } from './DisruptorPromoCard.types'

enum TileTypes {
  '1X1' = '1X1',
}

export const DisruptorPromoCard = ({
  thumbnail,
  icon,
  description,
  action,
  tileType,
  backgroundColor,
  textColor,
  promotionName,
  showGradient,
}: DisruptorPromoCardProps) => {
  const isGif = useMemo(() => {
    if (!thumbnail?.src) {
      return false
    }

    try {
      const url = new URL(thumbnail.src)

      return url.pathname.endsWith('.gif')
    } catch {}

    return false
  }, [thumbnail?.src])

  const renderImage = () => {
    if (!thumbnail?.src) {
      return null
    }

    if (isGif) {
      return (
        <Image
          src={getImageSrc(thumbnail.src)}
          addSrcSet={false}
          alt={thumbnail?.altText}
          width={thumbnail?.width}
          height={thumbnail?.height}
          className='w-full h-full object-cover'
        />
      )
    }

    return (
      <Image
        src={getImageSrc(thumbnail.src, '1440')}
        sources={
          <source
            srcSet={getImageSrc(thumbnail.src, '1024')}
            media='(max-width: 600px)'
          />
        }
        addSrcSet={false}
        alt={thumbnail?.altText}
        width={thumbnail?.width}
        height={thumbnail?.height}
        className='w-full h-full object-cover'
      />
    )
  }

  const renderOverlayLink = () => {
    if (!action?.href) {
      return null
    }

    return <Link href={action.href} className='absolute inset-0 z-10' />
  }

  const renderCtaLink = () => {
    if (!action?.href) {
      return null
    }

    return (
      <Link href={action.href} className={styles.action}>
        {action?.title && (
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegularBold}
            className='!text-[var(--color-text-primary)] !text-base !underline'>
            {action?.title}
          </Typography>
        )}
      </Link>
    )
  }

  return (
    <div
      className={classNames(
        'relative w-full h-full',
        'overflow-hidden rounded-md',
        {
          'col-span-1': tileType === TileTypes['1X1'],
        }
      )}
      style={{
        backgroundColor,
      }}>
      {renderOverlayLink()}
      {renderImage()}
      <div
        style={
          {
            '--color-text-primary': textColor,
          } as React.CSSProperties
        }
        className={classNames(
          'absolute inset-0 w-full h-full',
          'flex flex-col justify-between'
        )}>
        <div
          className={classNames('p-4 lg:p-6', {
            'bg-gradient-to-b from-black-75 via-black-65 to-transparent':
              showGradient,
          })}>
          {promotionName && (
            <Typography
              tag={TypographyTag.h3}
              variant={TypographyVariant.BodyXLargeBold}
              className='!text-[var(--color-text-primary)]'>
              {promotionName}
            </Typography>
          )}
          {(description as RichTextDocument)?.children?.length > 0 && (
            <div className={classNames('mt-6 mb-3', styles.description)}>
              <RichText content={description} />
            </div>
          )}
          {renderCtaLink()}
        </div>
        {icon?.src && (
          <div className={classNames('flex justify-center', 'p-4 lg:p-6')}>
            <Image
              src={getImageSrc(icon.src, '720')}
              sources={
                <source
                  srcSet={getImageSrc(icon.src, '600')}
                  media='(max-width: 600px)'
                />
              }
              alt={icon?.altText}
              width={icon.width}
              height={icon.height}
              className='w-full h-full'
              style={{ objectPosition: 'left', objectFit: 'contain' }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
